import React, { useEffect, useState } from "react";
import { DollarSign, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * HostAdSalesPanel — book advertiser slots on your radio station.
 *
 * Hosts book campaigns against an already-uploaded audio ad in the station's
 * track library. Admins approve or deny; approved bookings flow into the
 * existing radio ad queue for rotation.
 */
export default function HostAdSalesPanel({ slug, tracks = [] }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    advertiser_name: "", contact_email: "",
    track_id: "", rate_usd: 0, rotation_min: 15,
    start_at: todayIso(), end_at: isoDaysFromNow(14), notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/radio/ads/bookings", { params: { station_slug: slug } });
      setBookings(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async () => {
    if (!form.advertiser_name.trim() || !form.contact_email.trim() || !form.track_id) {
      toast.error("Advertiser, contact email and the ad track are required"); return;
    }
    try {
      await api.post("/radio/ads/bookings", { ...form, station_slug: slug, rate_usd: Number(form.rate_usd) || 0 });
      toast.success("Ad booking submitted for admin approval");
      setCreating(false);
      setForm({
        advertiser_name: "", contact_email: "", track_id: "", rate_usd: 0, rotation_min: 15,
        start_at: todayIso(), end_at: isoDaysFromNow(14), notes: "",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const byStatus = (s) => bookings.filter((b) => b.status === s);

  return (
    <div data-testid="host-ad-sales-panel" className="bg-[#0A0D14] border border-white/10 rounded-lg p-3 space-y-3">
      <div className="flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-emerald-400" />
        <div className="font-black text-sm flex-1">Advance ad sales</div>
        <button
          data-testid="host-ads-refresh"
          onClick={load}
          className="text-white/60 hover:text-white p-1"
        ><RefreshCw className="w-3.5 h-3.5" /></button>
        <button
          data-testid="host-ads-create-open"
          onClick={() => setCreating((v) => !v)}
          className="text-[10px] font-black bg-emerald-500 text-black px-2 py-1 rounded inline-flex items-center gap-1"
        ><Plus className="w-3 h-3" /> Book slot</button>
      </div>

      {creating && (
        <div className="bg-black/40 border border-white/10 rounded p-2 space-y-2" data-testid="host-ads-new-form">
          <input
            data-testid="host-ads-advertiser"
            placeholder="Advertiser (e.g. 'ATL Sneakers')"
            value={form.advertiser_name}
            onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          />
          <input
            data-testid="host-ads-email"
            placeholder="Advertiser contact email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          />
          <select
            data-testid="host-ads-track"
            value={form.track_id}
            onChange={(e) => setForm({ ...form, track_id: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          >
            <option value="">Pick the ad audio from the station library…</option>
            {tracks.map((t) => <option key={t.id} value={t.id}>{t.title} · {t.artist}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5">Rate (USD)</div>
              <input
                type="number" min="0"
                data-testid="host-ads-rate"
                value={form.rate_usd}
                onChange={(e) => setForm({ ...form, rate_usd: e.target.value })}
                className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
              />
            </label>
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5">Rotation (min)</div>
              <input
                type="number" min="1" max="120"
                data-testid="host-ads-rotation"
                value={form.rotation_min}
                onChange={(e) => setForm({ ...form, rotation_min: Number(e.target.value) || 15 })}
                className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
              />
            </label>
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5">Start</div>
              <input
                type="datetime-local"
                data-testid="host-ads-start"
                value={form.start_at.slice(0, 16)}
                onChange={(e) => setForm({ ...form, start_at: e.target.value + ":00Z" })}
                className="w-full bg-black/60 border border-white/10 rounded px-2 py-1 text-white text-xs"
              />
            </label>
            <label className="block">
              <div className="text-[9px] uppercase text-white/50 font-black mb-0.5">End</div>
              <input
                type="datetime-local"
                data-testid="host-ads-end"
                value={form.end_at.slice(0, 16)}
                onChange={(e) => setForm({ ...form, end_at: e.target.value + ":00Z" })}
                className="w-full bg-black/60 border border-white/10 rounded px-2 py-1 text-white text-xs"
              />
            </label>
          </div>
          <textarea
            placeholder="Notes for admin (spot length, campaign context, etc.)"
            data-testid="host-ads-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
          />
          <div className="flex gap-1.5">
            <button
              data-testid="host-ads-submit"
              onClick={submit}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs py-1.5 rounded"
            >Submit for approval</button>
            <button onClick={() => setCreating(false)} className="bg-white/5 hover:bg-white/15 text-white/80 text-xs px-3 rounded">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-1.5 text-center">
        {[
          ["requested", "Pending"],
          ["approved", "Approved"],
          ["live", "Live"],
          ["completed", "Done"],
        ].map(([k, label]) => (
          <div key={k} className="bg-white/5 rounded p-1.5">
            <div className="text-[9px] uppercase text-white/50 font-black">{label}</div>
            <div className="font-display font-black text-base">{byStatus(k).length}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-white/60 text-xs py-2">Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="text-white/40 text-xs text-center py-4">No bookings yet. Your first campaign is one click away.</div>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`host-ad-booking-${b.id}`} className="bg-white/5 border border-white/10 rounded p-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-bold text-sm flex-1 truncate">{b.advertiser_name}</div>
                <StatusBadge status={b.status} />
                <span className="text-emerald-300 font-black text-xs">${Number(b.rate_usd || 0).toFixed(2)}</span>
              </div>
              <div className="text-white/60 text-[11px] mt-0.5 truncate">
                {b.contact_email} · rotates every {b.rotation_min}m · {new Date(b.start_at).toLocaleDateString()} → {new Date(b.end_at).toLocaleDateString()}
              </div>
              {b.decision_note && <div className="text-white/50 text-[10px] italic mt-0.5">"{b.decision_note}"</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    requested: "bg-amber-500/25 text-amber-200",
    approved: "bg-sky-500/25 text-sky-200",
    live: "bg-emerald-500/25 text-emerald-200",
    completed: "bg-white/10 text-white/60",
    denied: "bg-red-500/25 text-red-200",
  };
  return <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${styles[status] || "bg-white/10"}`}>{status}</span>;
}

function todayIso() { const d = new Date(); d.setMinutes(0, 0, 0); return d.toISOString(); }
function isoDaysFromNow(n) { const d = new Date(); d.setDate(d.getDate() + n); d.setMinutes(0, 0, 0); return d.toISOString(); }
